/**
 * Composant MasterDetail réutilisable.
 *
 * - Wide/Medium : deux panneaux côte à côte (back-header masqué par CSS)
 * - Compact : navigation en pile avec transition animée
 *   Le master est visible par défaut ; quand hasSelection=true,
 *   le detail glisse par-dessus avec un header de retour.
 *
 * Le back-header est TOUJOURS rendu en HTML (pour éviter les bugs
 * d'apparition/animation), et masqué uniquement via CSS en wide/medium.
 */

import type { ReactNode } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { BackArrowIcon } from './Icons';

interface MasterDetailProps {
  master: ReactNode;
  detail: ReactNode;
  detailTitle?: ReactNode;
  detailHeaderRight?: ReactNode;
  hasSelection: boolean;
  onBack: () => void;
  className?: string;
}

export default function MasterDetail({
  master,
  detail,
  detailTitle,
  detailHeaderRight,
  hasSelection,
  onBack,
  className = '',
}: MasterDetailProps) {
  const { isCompact } = useResponsive();
  const showDetail = isCompact && hasSelection;

  return (
    <div className={`master-detail ${showDetail ? 'show-detail' : ''} ${className}`}>
      <div className="master-detail-master">
        {master}
      </div>
      <div className="master-detail-detail">
        <div className="master-detail-back-header">
          <button
            className="master-detail-back-button"
            onClick={onBack}
            aria-label="Retour"
          >
            <BackArrowIcon size={20} />
          </button>
          {detailTitle && (
            <div className="master-detail-back-title">
              {typeof detailTitle === 'string'
                ? <span className="master-detail-back-title-text">{detailTitle}</span>
                : detailTitle
              }
            </div>
          )}
          {detailHeaderRight && (
            <div className="master-detail-back-actions">{detailHeaderRight}</div>
          )}
        </div>
        {detail}
      </div>
    </div>
  );
}
